import { useState } from 'react';
import { Data, Schema, Predicate } from 'effect';
import { authClient } from '@/lib/auth-client';
import { clientEnv } from '@/env';
import { optionalCatching } from '@/lib/search-params';
import { persistSignupLanguageCookie } from './signup-language-cookie';

export type AuthUser = {
  userId: string;
  email: string;
  createdAt: Date | null;
};

export type AuthSession = {
  user: AuthUser;
};

export type LinkedProvider = {
  accountId: string;
  providerId: string;
  label: string;
};

export type PasswordResetParams = {
  token: string;
  newPassword: string;
};

export class BetterAuthError extends Data.TaggedError('BetterAuthError')<{
  userMessage: string;
  code?: string | undefined;
  status?: number;
}> {}

/**
 * Search params `signInSocial` asks better-auth to redirect back with, so the
 * landing route can tell a new account from a returning one and say which
 * provider it came from.
 *
 * Needed because social sign-in is a full-page redirect: the sign-in call never
 * resolves in the tab that started it, so nothing on the way out can report the
 * outcome. better-auth decides which of the two callback URLs to send the
 * browser to from `isRegister` (true only when the OAuth callback had to create
 * the user row — linking a new provider to an existing account is false), which
 * is the only trustworthy new-vs-returning signal available on this path.
 */
const AUTH_RESULTS = ['signup', 'login'] as const;
const AUTH_PROVIDERS = ['google', 'apple'] as const;

/** Whether a completed sign-in created the account or matched an existing one. */
export type AuthResult = (typeof AUTH_RESULTS)[number];
export type AuthProvider = (typeof AUTH_PROVIDERS)[number];

/** The marker keys, so the landing route can clear exactly what it read. */
export const AUTH_MARKER_SEARCH_KEYS = [
  'auth_result',
  'auth_provider',
] as const;

/**
 * `search` updater that drops the markers and leaves every other param alone.
 *
 * Removes the keys rather than setting them to `undefined`, so nothing is left
 * behind in the validated search.
 */
export const stripAuthMarkers = (prev: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(prev).filter(
      ([key]) => !(AUTH_MARKER_SEARCH_KEYS as readonly string[]).includes(key),
    ),
  );

/**
 * The `redirectTo` value `_authenticated`'s guard hands to `/app/login` when it
 * ejects an unauthenticated visitor.
 *
 * Keeps the rest of the query — utm tags, promo codes, whatever a link carried
 * — but drops the auth markers. They describe a sign-in that already happened;
 * carrying them through the login page replays them against whatever sign-in
 * happens next, so opening a marked URL without a session and then signing in
 * with email/password would report two `auth_success` events, the second
 * claiming a Google signup.
 */
export const loginRedirectTarget = (location: {
  pathname: string;
  searchStr: string;
}) => {
  const search = new URLSearchParams(location.searchStr);
  for (const key of AUTH_MARKER_SEARCH_KEYS) search.delete(key);
  const query = search.toString();
  return `${location.pathname}${query ? `?${query}` : ''}`;
};

/**
 * `validateSearch` for the markers, declared on the `_authenticated` layout
 * route so every route a redirect can land on inherits it. Read and cleared by
 * `useAuthSessionAnalytics`.
 *
 * Total by construction: a stale or hand-edited value drops to absent rather
 * than failing validation, which would trade the whole authenticated shell for
 * an error component over a telemetry param.
 */
export const authResultSearchSchema = Schema.toStandardSchemaV1(
  Schema.Struct({
    auth_result: optionalCatching(Schema.Literals(AUTH_RESULTS)),
    auth_provider: optionalCatching(Schema.Literals(AUTH_PROVIDERS)),
  }),
);

/** Where better-auth sends the browser once the OAuth round-trip completes. */
const SOCIAL_LANDING_PATH = '/app/home';

/**
 * A landing URL carrying the markers.
 *
 * Kept to bare `[a-z_]` keys and literal values on a relative path: better-auth
 * runs both callback URLs through its trusted-origin check before it will
 * accept them, and it rejects a relative path containing anything exotic. A
 * rejected URL fails the sign-in outright with `INVALID_NEW_USER_CALLBACK_URL`
 * — loud, not silent — but there is no reason to go near that edge.
 *
 * `landingPath` is caller-supplied so a flow that owns its own post-signup
 * screen can keep the user in it (the funnel's paywall step, or its account
 * step under `?flow=`). A query it already carries is kept, with the same
 * `[a-z_]`-and-literals discipline; the markers are appended after it.
 */
const socialCallbackUrl = (
  result: AuthResult,
  provider: AuthProvider,
  landingPath: string = SOCIAL_LANDING_PATH,
) =>
  `${landingPath}${landingPath.includes('?') ? '&' : '?'}auth_result=${result}&auth_provider=${provider}`;

export type SocialRedirectTargets = {
  newUserPath?: string | undefined;
  returningUserPath?: string | undefined;
  /**
   * Where `/auth-error`'s "try again" sends the user. Defaults to the login
   * page; a flow with its own signup screen should pass that screen.
   */
  errorReturnPath?: string | undefined;
};

/**
 * Route that renders a failed OAuth round-trip. See `routes/auth-error.tsx`.
 *
 * Kept in step by hand with `onAPIError.errorURL` in `packages/core/auth` —
 * that one is the server-side default for failures with no flow state left to
 * read, and the two packages share no module.
 */
const AUTH_ERROR_PATH = '/auth-error';

/** Where "try again" goes when the caller named no flow of its own. */
export const AUTH_ERROR_DEFAULT_RETURN = '/app/login';

/**
 * The `errorCallbackURL` better-auth redirects to when the OAuth round-trip
 * fails. better-auth appends its own `?error=<code>` (and sometimes
 * `&error_description=`), so this only carries where to send the user next.
 *
 * Same trusted-origin constraint as `socialCallbackUrl` — the relative-path
 * check allows `/`, `=` and `&` inside the query, so a bare path value passes.
 * Callers pass literals, never user input; a `returnTo` carrying its own query
 * would produce two `?` and fail that check.
 */
const authErrorUrl = (returnTo: string = AUTH_ERROR_DEFAULT_RETURN) =>
  `${AUTH_ERROR_PATH}?return_to=${returnTo}`;

export async function signInEmail(params: {
  email: string;
  password: string;
}): Promise<AuthSession> {
  const { data, error } = await authClient.signIn.email({
    email: params.email,
    password: params.password,
  });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Sign in failed',
      code: error.code,
      status: error.status,
    });
  }
  return {
    user: {
      userId: data.user.id,
      email: data.user.email,
      createdAt: new Date(data.user.createdAt),
    },
  };
}

export async function signUpEmail(params: {
  email: string;
  password: string;
}): Promise<AuthSession> {
  persistSignupLanguageCookie();
  const { data, error } = await authClient.signUp.email({
    name: params.email,
    email: params.email,
    password: params.password,
  });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Sign up failed',
      code: error.code,
      status: error.status,
    });
  }
  return {
    user: {
      userId: data.user.id,
      email: data.user.email,
      createdAt: new Date(data.user.createdAt),
    },
  };
}

/**
 * Never resolves — every path out of here leaves the document. Returning
 * `never` is what stops callers from inventing a `signup`/`login` answer this
 * function cannot know; the outcome is reported on the landing route instead.
 */
export async function signInSocial(
  provider: AuthProvider,
  targets: SocialRedirectTargets = {},
): Promise<never> {
  persistSignupLanguageCookie();
  const { data, error } = await authClient.signIn.social({
    provider,
    callbackURL: socialCallbackUrl(
      'login',
      provider,
      targets.returningUserPath,
    ),
    newUserCallbackURL: socialCallbackUrl(
      'signup',
      provider,
      targets.newUserPath,
    ),
    // Without this better-auth falls back to its own `/api/auth/error`, which
    // in production 302s to `/?error=<code>` — a silent bounce to the homepage
    // with nothing to explain it. See `routes/auth-error.tsx`.
    errorCallbackURL: authErrorUrl(targets.errorReturnPath),
  });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Social sign in failed',
      code: error.code,
      status: error.status,
    });
  }
  // Social sign-in is a FULL-PAGE redirect to the OAuth provider — the response
  // is `{ redirect: true, url }` with no inline user. better-auth's built-in
  // redirect fetch-plugin already set `window.location.href = url`; we set it
  // again defensively, in case that plugin is ever disabled.
  //
  // The one response shape carrying no `url` is better-auth's `idToken` variant,
  // which establishes the session inline. We never send an `idToken`, so that
  // branch is unreachable today; it navigates to the callback itself so both
  // paths land on a marked URL. It marks `login` because an inline response
  // gives us no `isRegister` — revisit that if id-token sign-in is ever enabled.
  window.location.href =
    data && 'url' in data && data.url
      ? data.url
      : socialCallbackUrl('login', provider, targets.returningUserPath);

  // Leaving the promise pending keeps the auth mutation in its `pending` state,
  // so the submit/OAuth buttons hold their loading state until the browser
  // unloads — otherwise the mutation settles, the form re-renders idle, and it
  // flashes "as if nothing happened" mid-redirect.
  return new Promise<never>(() => {
    // intentionally never resolves — the page is navigating away
  });
}

export async function signOut(): Promise<void> {
  await authClient.signOut();
}

export async function requestPasswordReset(email: string): Promise<void> {
  const { error } = await authClient.requestPasswordReset({
    email,
    redirectTo: `${clientEnv.siteUrl}/password-reset`,
  });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Password reset request failed',
      code: error.code,
      status: error.status,
    });
  }
}

export async function completePasswordReset(
  params: PasswordResetParams,
): Promise<void> {
  const { error } = await authClient.resetPassword({
    newPassword: params.newPassword,
    token: params.token,
  });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Password reset failed',
      code: error.code,
      status: error.status,
    });
  }
}

export async function linkSocialAccount(
  provider: 'google' | 'apple',
): Promise<void> {
  const { error } = await authClient.linkSocial({ provider });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Failed to link account',
      code: error.code,
      status: error.status,
    });
  }
}

export async function unlinkSocialAccount(accountId: string): Promise<void> {
  const { error } = await authClient.unlinkAccount({ accountId });
  if (error) {
    throw new BetterAuthError({
      userMessage: error.message ?? 'Failed to unlink account',
      code: error.code,
      status: error.status,
    });
  }
}

export async function getLinkedProviders(): Promise<Array<LinkedProvider>> {
  const { data } = await authClient.listAccounts();
  if (!data) return [];
  return data.map((account) => ({
    accountId: account.id,
    providerId: account.providerId,
    label:
      account.providerId === 'google'
        ? 'Google'
        : account.providerId === 'apple'
          ? 'Apple'
          : account.providerId,
  }));
}

/**
 * Keeps better-auth's session store subscribed for as long as the caller is
 * mounted, without reading it.
 *
 * The store only tracks the session while something is subscribed to it. When
 * its last subscriber unmounts it goes dormant: it KEEPS its final value, and
 * its cleanup drops the `$sessionSignal` listener that `/sign-in/email`,
 * `/sign-up/email` and `/sign-out` flip — so a dormant store never learns that
 * the session changed, and hands its frozen value to whatever mounts next
 * (it only refetches from a `setTimeout(…, 0)`, which lands after that
 * component has rendered and run its effects).
 *
 * Logging out unmounts the last subscriber in the `/app` subtree, freezing the
 * store on the signed-out session. Held open across the whole subtree instead,
 * so a sign-in in the same page load is observed rather than missed — by every
 * consumer of the store, not just the routes that act on it.
 *
 * @see test/e2e/flows/reauth-after-logout.spec.ts
 */
export function useKeepAuthSessionSubscribed(): void {
  authClient.useSession();
}

export function useAuthSession(): {
  user: AuthUser | null;
  isPending: boolean;
  hasError: boolean;
  errorStatus: number | null;
} {
  const { data, error, isPending } = authClient.useSession();
  const [settled, setSettled] = useState(!isPending);
  if (!isPending && !settled) setSettled(true);
  const errorStatus =
    Predicate.isObjectOrArray(error) &&
    'status' in error &&
    Predicate.isNumber(error.status)
      ? error.status
      : null;
  return {
    user: data?.user
      ? {
          userId: data.user.id,
          email: data.user.email,
          createdAt: new Date(data.user.createdAt),
        }
      : null,
    isPending: isPending && !settled,
    hasError: error !== null,
    errorStatus,
  };
}
